import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { PageHeader, Badge, Empty, Stat } from "@/components/admin/Ui";
import { LinkedinLogo, PaperPlaneTilt, ChatCircle, UserPlus, ArticleNyTimes, Trash, Buildings, User } from "@phosphor-icons/react";

const KIND_META = {
  invites: { icon: UserPlus, label: "Connect invite" },
  dms: { icon: PaperPlaneTilt, label: "Direct message" },
  posts: { icon: ArticleNyTimes, label: "Post" },
  comments: { icon: ChatCircle, label: "Comment" },
};

const blankForKind = (k) => ({
  kind: k, as_page: false, target_profile: "", target_post_url: "", note: "", body: "",
});

export default function LinkedIn() {
  const [status, setStatus] = useState(null);
  const [actions, setActions] = useState([]);
  const [tab, setTab] = useState("invites");
  const [form, setForm] = useState(blankForKind("invites"));
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const [s, a] = await Promise.all([
      api.get("/linkedin/status"),
      api.get("/linkedin/actions", { params: { kind: tab } }),
    ]);
    setStatus(s.data);
    setActions(a.data.actions || []);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab]);

  const switchTab = (k) => { setTab(k); setForm(blankForKind(k)); };

  const queue = async () => {
    setBusy(true);
    try {
      await api.post("/linkedin/queue", form);
      toast.success(status?.live ? "Action sent via Unipile" : "Action queued (MOCKED — Unipile not configured)");
      setForm(blankForKind(tab));
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Queue failed"); }
    finally { setBusy(false); }
  };

  const del = async (id) => {
    await api.delete(`/linkedin/actions/${id}`);
    await load();
  };

  if (!status) return <div className="p-8 text-navy/60">Loading…</div>;

  const usage = status.usage_today || {};
  const caps = status.caps || {};

  return (
    <div data-testid="admin-linkedin-page">
      <PageHeader title="LinkedIn operations" subtitle="Page Engagement · Outbound · Comments" action={
        status.live
          ? <Badge tone="green">LIVE · Unipile connected</Badge>
          : <Badge tone="amber">MOCKED · Awaiting Unipile credentials</Badge>
      } />
      <div className="p-8 space-y-6">
        {/* Daily caps */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {Object.entries(caps).map(([k, cap]) => {
            const used = usage[k] || 0;
            const pct = Math.min(100, (used / Math.max(1, cap)) * 100);
            return (
              <div key={k} className="bg-white border border-black/10 p-4" data-testid={`linkedin-cap-${k}`}>
                <div className="overline">{k}</div>
                <div className="mt-2 flex items-end gap-2">
                  <div className="ship-number text-3xl font-black text-navy leading-none">{used}</div>
                  <div className="text-sm text-navy/50 pb-1">/ {cap}</div>
                </div>
                <div className="h-1 mt-3 bg-navy/10">
                  <div className={`h-1 ${pct > 90 ? "bg-red-500" : pct > 70 ? "bg-amber-500" : "bg-ember"}`} style={{ width: `${pct}%` }} />
                </div>
              </div>
            );
          })}
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-2">
          {Object.entries(KIND_META).map(([k, m]) => {
            const Icon = m.icon;
            return (
              <button
                key={k}
                data-testid={`linkedin-tab-${k}`}
                onClick={() => switchTab(k)}
                className={`flex items-center gap-2 text-xs uppercase tracking-widest px-3 py-2 border transition-colors ${
                  tab === k ? "bg-navy text-white border-navy" : "bg-white text-navy/70 border-black/15 hover:border-navy"
                }`}
              >
                <Icon size={14} /> {m.label}
              </button>
            );
          })}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Composer */}
          <div className="lg:col-span-5 bg-white border border-black/10 p-6 space-y-3" data-testid="linkedin-composer">
            <div className="flex items-center gap-2 mb-1">
              <LinkedinLogo size={18} weight="duotone" className="text-ember" />
              <div className="overline">{KIND_META[tab].label}</div>
            </div>

            <label className="flex items-center gap-3 text-sm text-navy/80">
              <div className="inline-flex border border-black/15 rounded-none overflow-hidden text-xs uppercase tracking-widest">
                <button data-testid="linkedin-as-person" onClick={() => setForm({ ...form, as_page: false })} className={`px-3 py-1.5 flex items-center gap-1.5 ${!form.as_page ? "bg-navy text-white" : "bg-white text-navy/70"}`}>
                  <User size={12} /> As Rok Kobal
                </button>
                <button data-testid="linkedin-as-page" onClick={() => setForm({ ...form, as_page: true })} className={`px-3 py-1.5 flex items-center gap-1.5 border-l border-black/15 ${form.as_page ? "bg-navy text-white" : "bg-white text-navy/70"}`}>
                  <Buildings size={12} /> As EP air&amp;sea
                </button>
              </div>
            </label>

            {(tab === "invites" || tab === "dms") && (
              <Field label="Target LinkedIn profile URL">
                <input data-testid="linkedin-target-profile" value={form.target_profile} onChange={(e) => setForm({ ...form, target_profile: e.target.value })} className={input} placeholder="https://www.linkedin.com/in/..." />
              </Field>
            )}
            {tab === "invites" && (
              <Field label="Invite note (optional · 300 chars max)">
                <textarea data-testid="linkedin-note" maxLength={300} rows={4} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className={input} />
              </Field>
            )}
            {tab === "dms" && (
              <Field label="Message">
                <textarea data-testid="linkedin-body" rows={6} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} className={input} />
              </Field>
            )}
            {tab === "comments" && (
              <>
                <Field label="Target post URL">
                  <input data-testid="linkedin-target-post" value={form.target_post_url} onChange={(e) => setForm({ ...form, target_post_url: e.target.value })} className={input} placeholder="https://www.linkedin.com/posts/..." />
                </Field>
                <Field label="Comment text">
                  <textarea data-testid="linkedin-body" rows={4} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} className={input} />
                </Field>
              </>
            )}
            {tab === "posts" && (
              <Field label="Post body">
                <textarea data-testid="linkedin-body" rows={10} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} className={input} placeholder="Write your post (will publish under selected identity)" />
              </Field>
            )}

            <button onClick={queue} disabled={busy} data-testid="linkedin-queue-button" className="btn-primary w-full justify-center mt-2">
              <PaperPlaneTilt size={14} /> {busy ? "Queueing…" : (status.live ? "Send via Unipile" : "Queue (mock)")}
            </button>
            {!status.live && (
              <p className="text-[10px] text-amber-700 mt-1 leading-relaxed">
                LinkedIn live wiring is paused — provide Unipile creds via <code>.env</code> (UNIPILE_DSN, UNIPILE_API_KEY, LINKEDIN_ACCOUNT_ID, LINKEDIN_ORG_URN) and restart backend to switch from mock to live.
              </p>
            )}
          </div>

          {/* Queue / history */}
          <div className="lg:col-span-7 bg-white border border-black/10" data-testid="linkedin-queue-panel">
            <div className="px-5 py-3 border-b border-black/10 overline">{KIND_META[tab].label} · history ({actions.length})</div>
            {actions.length === 0 ? <div className="p-6"><Empty label={`No ${tab} yet.`} /></div> : (
              <ul className="divide-y divide-black/5 max-h-[70vh] overflow-y-auto">
                {actions.map((a) => (
                  <li key={a.id} className="px-5 py-3 flex items-start gap-3" data-testid={`linkedin-action-${a.id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge tone={a.status === "sent" ? "green" : "amber"}>{a.status}</Badge>
                        {a.mocked && <Badge tone="neutral">mocked</Badge>}
                        {a.as_page ? <Badge tone="ember">as page</Badge> : <Badge tone="neutral">as Rok</Badge>}
                        <span className="text-[10px] uppercase tracking-widest text-navy/50">{new Date(a.created_at).toLocaleString()}</span>
                      </div>
                      <div className="text-sm text-navy mt-1 truncate">{a.target_profile || a.target_post_url || "—"}</div>
                      {a.note && <div className="text-xs text-navy/70 mt-1 italic">"{a.note.slice(0, 140)}"</div>}
                      {a.body && <div className="text-xs text-navy/70 mt-1 whitespace-pre-wrap line-clamp-3">{a.body}</div>}
                    </div>
                    <button onClick={() => del(a.id)} data-testid={`linkedin-delete-${a.id}`} className="text-red-600 hover:text-red-700 mt-1">
                      <Trash size={14} />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

const input = "w-full border border-black/15 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-navy";
const Field = ({ label, children }) => (
  <div><label className="overline mb-2 block">{label}</label>{children}</div>
);
